/***********************
 * DEVICE IDENTIFICATION TABLE
 ************************/
import React, { useEffect, useState } from "react"
import { Row, Col, Button } from "reactstrap"
import PropTypes from "prop-types"

//Translation
import { withTranslation } from "react-i18next"
import {
  stringSorter,
  getOperatorByIccid,
  operatorFormatter,
  numberSorter,
  numStringSorter,
  booleanSorter,
} from "utils/general"
import TableReporting from "components/Tables/TableReporting"
import { columnWidth } from "components/Tables/columns"

const DeviceIdentificationsTable = props => {
  const titleExport = props.t("Device identifications")
  //Table data state
  const [series, setSeries] = useState([])
  const [origSeries, setOrigSeries] = useState([])
  // Expanded table keys
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  //Filtered data
  const [filteredData, setFilteredData] = useState(series)
  const [handleValue, setHandleValue] = useState("")
  const [loading, setLoading] = useState(true)

  //Setting data state
  useEffect(() => {
    if (props.data?.list && props.data.list !== series) {
      let sorted = [...props.data.list].sort((a, b) => {
        return stringSorter(a, b, "clientName")
      })
      let parsedData = sorted.map(item => {
        if (item.carephoneAccessories) {
          return {
            ...item,
            carephoneAccessories: JSON.parse(item.carephoneAccessories),
          }
        }
        return item
      })
      setOrigSeries(parsedData)
      setSeries(parsedData)
      setLoading(false)
    }
  }, [props.data])

  useEffect(() => {
    // console.log("entries filtered", series)
    if (series != undefined) {
      setFilteredData(series)
      if (handleValue != "") {
        handlingInput(handleValue)
      }
    }
  }, [series])

  // Export values
  const pdfHeading = [
    props.t("Group"),
    props.t("Client"),
    props.t("Gateway"),
    props.t("Tracker"),
    props.t("Safety phone"),
    props.t("Safety phone extentions"),
    //props.t("Medication reminder"),
    props.t("Video"),
    props.t("Group call"),
    props.t("External speaker"),
    props.t("Smartlocks"),
    props.t("Measuring devices"),
  ]

  const formatDeviceAndOperatorExport = (device, iccid) => {
    if (device == null) return ""

    if (iccid == null) {
      return `${device}`
    }

    return `${device}, ${getOperatorString(iccid)}`
  }

  const pdfData = filteredData.map(elt => [
    elt.group,
    elt.clientName,
    formatDeviceAndOperatorExport(elt.gateway, elt.gatewayIccid),
    // statusFormatterExport(elt.tracker),
    formatDeviceAndOperatorExport(elt.tracker, elt.trackerIccid),
    formatDeviceAndOperatorExport(elt.carephone, elt.carephoneIccid),
    devicesFormatterExport(elt.carephoneAccessories),
    //elt.dose,
    formatDeviceAndOperatorExport(formatVideoData(elt.video), elt.videoIccid),
    statusFormatterExport(elt.videoGroupCall),
    statusFormatterExport(elt.externalSpeaker),
    formatDeviceAndOperatorExport(elt.smartlock, elt.smartlockIccid),
    elt.measuringDevices,
  ])

  const xlsxHeading = [pdfHeading]

  const xlsxData = filteredData.map(elt => [
    elt.group,
    elt.clientName,
    formatDeviceAndOperatorExport(elt.gateway, elt.gatewayIccid),
    // statusFormatterExport(elt.tracker),
    formatDeviceAndOperatorExport(elt.tracker, elt.trackerIccid),
    formatDeviceAndOperatorExport(elt.carephone, elt.carephoneIccid),
    devicesFormatterExport(elt.carephoneAccessories),
    //elt.dose,
    formatDeviceAndOperatorExport(formatVideoData(elt.video), elt.videoIccid),
    statusFormatterExport(elt.videoGroupCall),
    statusFormatterExport(elt.externalSpeaker),
    formatDeviceAndOperatorExport(elt.smartlock, elt.smartlockIccid),
    elt.measuringDevices,
  ])

  const columnStyle = {
    0: { cellWidth: 75 }, // group
    1: { cellWidth: 75 }, // clientName
    2: { cellWidth: 48 }, // gateway
    3: { cellWidth: 60 }, // tracker
    4: { cellWidth: 55 }, // carephone
    5: { cellWidth: 90 }, // carephoneAccessories
    6: { cellWidth: 100 }, // video
    7: { cellWidth: 48 }, // videoGroupCall
    8: { cellWidth: 65 }, // externalSpeaker
    9: { cellWidth: 55 }, // smartlock
    10: { cellWidth: 65 }, // measuringDevices
  }

  // Handle search function
  const handlingInput = value => {
    let result = series.filter(data => {
      const searchLower = value.toLowerCase().trim()

      // Get operator value
      const operator = getOperatorString(data.videoIccid).toLowerCase()

      return (
        data.clientName?.toLowerCase().includes(searchLower) ||
        data.group?.toLowerCase().includes(searchLower) ||
        data.gateway?.toLowerCase().includes(searchLower) ||
        data.tracker?.toLowerCase().includes(searchLower) ||
        data.carephone?.toLowerCase().includes(searchLower) ||
        //data.dose?.toLowerCase().includes(searchLower) ||
        data.smartlock?.toLowerCase().includes(searchLower) ||
        data.measuringDevices?.toLowerCase().includes(searchLower) ||
        data.video?.toLowerCase().includes(searchLower) ||
        operator?.includes(searchLower) ||
        // Search for External speaker
        (searchLower == props.t("External speaker").toLowerCase().trim() &&
          data.externalSpeaker === true) ||
        data.externalSpeaker?.toString().toLowerCase().includes(searchLower) ||
        // Search for Group call
        (searchLower == props.t("Group call").toLowerCase().trim() &&
          data.videoGroupCall === true) ||
        data.videoGroupCall?.toString().toLowerCase().includes(searchLower)
      )
    })

    setFilteredData(result)

    // Set original data result if value ""
    if (value == "") {
      setFilteredData(series)
    }
  }

  // Handle search function
  const handleSearch = event => {
    let value = event.target.value.toLowerCase()
    setHandleValue(value)
    handlingInput(value)
  }

  const handleExpand = (event, record) => {
    event.preventDefault()
    const keys = [...expandedRowKeys]
    const index = keys.indexOf(record.id) // Use id as a key
    if (index >= 0) {
      keys.splice(index, 1) // Delete id/key if included
    } else {
      keys.length = 0 // Delete all id/key
      keys.push(record.id) // Add id/key
    }
    setExpandedRowKeys(keys)
  }

  // Table structure
  const columns = [
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b) => {
        return stringSorter(a, b, "group")
      },
      ellipsis: true,
      width: columnWidth.group,
    },
    {
      dataIndex: "clientName",
      title: props.t("Client"),
      sorter: (a, b) => {
        return stringSorter(a, b, "clientName")
      },
      width: columnWidth.name,
      ellipsis: true,
    },
    {
      dataIndex: "gateway",
      title: props.t("Gateway"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["gateway"], b["gateway"], sortOrder)
      },
      render: (values, record) => {
        const operator = getOperatorString(record.gatewayIccid) // Get operator value
        const operatorOutput = operatorFormatter(operator) // Format operator

        return (
          <span>
            {values} &nbsp; {operatorOutput}
          </span>
        )
      },

      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "tracker",
      title: props.t("Tracker"),
      sorter: (a, b) => {
        return numStringSorter(a["tracker"], b["tracker"])
      },
      render: (values, record) => {
        const trackerOutput = statusFormatter(values) // Get the sn/imei based on device type
        const operator = getOperatorString(record.trackerIccid) // Get operator value
        const operatorOutput = operatorFormatter(operator) // Format operator
        return (
          <span>
            {trackerOutput} &nbsp; {operatorOutput}
          </span>
        )
      },
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "carephone",
      title: props.t("Safety phone"),
      width: "230px",
      ellipsis: true,
      sorter: (a, b, sortOrder) =>
        numberSorter(a.carephone, b.carephone, sortOrder),

      render: (text, record) => {
        if (text === undefined) return null

        const extensionsCount = record.carephoneAccessories?.length ?? 0
        const isExpanded = expandedRowKeys.includes(record.id)
        const operator = getOperatorString(record.carephoneIccid) // Get operator value
        const operatorOutput = operatorFormatter(operator) // Format operator

        return (
          <>
            {extensionsCount > 0 && (
              <span className="float-end">
                <Button
                  className="m-0 py-0 px-1"
                  size="sm"
                  color="primary"
                  onClick={event => {
                    event.stopPropagation()
                    handleExpand(event, record)
                  }}
                >
                  {isExpanded
                    ? props.t("Hide")
                    : `${props.t("Extentions")} (${extensionsCount})`}
                </Button>
              </span>
            )}
            <span>
              {text} &nbsp; {operatorOutput}
            </span>
          </>
        )
      },
    },
    /*{
      dataIndex: "dose",
      title: props.t("Medication reminder"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["dose"], b["dose"], sortOrder)
      },
      width: columnWidth.medium,
      ellipsis: true,
    },*/
    {
      dataIndex: "video",
      title: props.t("Video"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["video"], b["video"], sortOrder)
      },
      render: (videoValues, record) => {
        const videoOutput = formatVideoData(videoValues) // Get the sn/imei based on device type
        const operator = getOperatorString(record.videoIccid) // Get operator value
        const operatorOutput = operatorFormatter(operator) // Format operator

        return (
          <span>
            {/* {record.videoGroupCall && (
              <i className="me-1 mdi mdi-account-group text-primary" />
            )} */}
            {videoOutput} &nbsp; {operatorOutput}
          </span>
        )
      },
      width: "240px",
      ellipsis: true,
    },
    {
      dataIndex: "videoGroupCall",
      title: props.t("Group call"),
      width: "140px",
      ellipsis: true,
      render: (value, record, index) => {
        return (
          <span>
            {record.videoGroupCall && (
              <span className="badge bg-success font-size-12">
                {props.t("In use")} <i className="bx bx-check"></i>
              </span>
            )}
          </span>
        )
      },
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "videoGroupCall", sortOrder)
      },
    },
    {
      dataIndex: "externalSpeaker",
      title: props.t("External speaker"),
      render: value => statusFormatterBoolean(value),
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "externalSpeaker", sortOrder)
      },
      width: "150px",
      ellipsis: true,
    },
    {
      dataIndex: "smartlock",
      title: props.t("Smartlocks"),
      sorter: (a, b) => {
        return numStringSorter(a["smartlock"], b["smartlock"])
      },
      render: (values, record) => {
        const operator = getOperatorString(record.smartlockIccid) // Get operator value
        const operatorOutput = operatorFormatter(operator) // Format operator
        return (
          <span>
            {values} &nbsp; {operatorOutput}
          </span>
        )
      },
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "measuringDevices",
      title: props.t("Measuring devices"),
      sorter: (a, b) => {
        return stringSorter(a, b, "measuringDevices")
      },
      width: "140px",
      ellipsis: true,
    },
  ]

  const expandedRowRender = record => {
    return (
      <div className="extra-table-row-background px-3">
        <h6 className=" mb-3">
          {props.t("Safety phone extentions for")}{" "}
          <b className="text-muted">{record.carephone}</b>
        </h6>
        <div style={{ width: "100vh" }}>
          <Row>
            {record.carephoneAccessories.map((e, index) => {
              return (
                <Col xs="4" key={e.radioId}>
                  <Row>
                    <Col xs="2">
                      <div className="">{index + 1}.</div>
                    </Col>
                    <Col xs="10">
                      <div>{e.equipmentName}</div>
                      <div>
                        {props.t("ID")}: {e.radioId}
                      </div>
                    </Col>
                  </Row>
                </Col>
              )
            })}
          </Row>
        </div>
      </div>
    )
  }

  const expandable = {
    expandedRowRender: expandedRowRender,
    rowExpandable: record => record.carephone !== undefined,
    expandedRowKeys: expandedRowKeys,
    onExpand: (expanded, record) => handleExpand(e, record),
    showExpandColumn: false,
  }

  function statusFormatter(cell) {
    if (cell == "<N/A>") {
      return (
        <span className="badge bg-tertiary font-size-12">
          {props.t("No identification")}
        </span>
      )
    }
    if (cell != "<N/A>") {
      return cell
    }
  }

  function statusFormatterBoolean(cell) {
    return cell ? (
      <span className="badge bg-success font-size-12">
        {props.t("In use")} <i className="bx bx-check"></i>
      </span>
    ) : (
      <span className="badge bg-danger font-size-12"></span> // HOX: Empty span
    )
  }

  function statusFormatterExport(cell) {
    return cell ? props.t("x") : ""
  }

  // format Carephone devices
  function devicesFormatterExport(cell) {
    if (cell == undefined) {
      return ""
    }
    if (cell != undefined) {
      let formattedDevices = cell.map((ce, index) => {
        return (
          `${index + 1}. ${props.t("Device name")}: ${
            ce.equipmentName
          }, ${props.t("Device id")}: ${ce.radioId}` + " "
        )
      })
      return formattedDevices.join("\n")
    }
  }

  // Format video data
  function formatVideoData(videoValues) {
    if (!videoValues) return ""

    // Extract sn and imei values
    const snMatch = videoValues.match(/sn:\s*([^,]+)/)
    const imeiMatch = videoValues.match(/imei:\s*([^,]+)/)
    let videoOutput = ""

    if (snMatch) {
      const snValue = snMatch[1]
      // If sn starts with "R", use sn
      if (snValue.startsWith("R")) {
        videoOutput = `sn: ${snValue}`
        // If sn starts with "PP" or "EH", use imei
      } else if (snValue.startsWith("PP") || snValue.startsWith("EH")) {
        videoOutput = imeiMatch ? `imei: ${imeiMatch[1]}` : ""
      }
    }

    return `${videoOutput}`.trim()
  }

  function getOperatorString(iccid) {
    // Extract videoIccid from the videoValues and use it to determine the operator
    const operator = getOperatorByIccid(iccid)
    return operator.trim()
  }

  return (
    <>
      <TableReporting
        origSeries={origSeries}
        setSeries={setSeries}
        title={titleExport}
        columns={columns}
        seriesFiltered={filteredData}
        xlsxHeading={xlsxHeading}
        xlsxData={xlsxData}
        pdfHeading={pdfHeading}
        pdfData={pdfData}
        handleSearch={handleSearch}
        loading={loading}
        expandable={expandable}
        pdfColumnStyle={columnStyle}
      ></TableReporting>
    </>
  )
}

DeviceIdentificationsTable.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
}

export default withTranslation()(DeviceIdentificationsTable)
